import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Inject, OnDestroy, OnInit, computed, signal, Input, Output, EventEmitter } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { DOCUMENT } from '@angular/common';
import { distinctUntilChanged, filter, map, switchMap, tap, ReplaySubject, merge, EMPTY, Subscription } from 'rxjs';
import { ImageVisualizerComponent } from '../image-visualizer/image-visualizer.component';
import { CoordinateSpace, ImageSize, ImageVisualizerBox } from '../image-visualizer/image-visualizer.types';
import { RecognitionSystemLibraryService } from '../../../recongition-system-api/recongnition-library';
import { ImageRecognitionExtend, RecognitionDetails } from '../../../recongition-system-api/model';
import { MatTabsModule } from '@angular/material/tabs';
import { MeasurementsPanelComponent } from '../measurements-panel/measurements-panel.component';
import { DetectionsPanelComponent } from '../detections-panel/detections-panel.component';
import { MetadataCardComponent } from '../metadata-card/metadata-card.component';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { MatMenuModule } from '@angular/material/menu';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatSnackBar } from '@angular/material/snack-bar';
import { PageVisualizerService } from '../page-visualizer.service';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';

@Component({
  selector: 'app-page-visualizer-client',
  standalone: true,
  imports: [
    CommonModule,
    ImageVisualizerComponent,
    MatTabsModule,
    MetadataCardComponent,
    MeasurementsPanelComponent,
    DetectionsPanelComponent,
    MatButtonModule,
    MatIconModule,
    MatMenuModule,
    MatTooltipModule,
    MatProgressSpinnerModule,
  ],
  templateUrl: './page-visualizer-client.component.html',
  styleUrl: './page-visualizer-client.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PageVisualizerClientComponent {
  @Input() set eventId(val: string) {
    if (val) this.manualId$.next(val);
  }
  @Input() isDialogMode = false;
  @Output() recommendedHeight = new EventEmitter<string>();
  @Output() closeRequest = new EventEmitter<void>();
  // ReplaySubject(1): guarda el último ID emitido para que ngOnInit
  // (que corre después del setter de @Input) lo reciba aunque llegue tarde.
  private readonly manualId$ = new ReplaySubject<string>(1);
  private readonly subs = new Subscription();
  readonly coordinateSpace: CoordinateSpace = 'image';
  readonly fields_metadata:any = [{
    
    key: 'taskId',
    normaliceKey: 'taskId',
    order:1,
    label: 'ID Tarea',
  },{
    
    key: 'codigoNatural',
    normaliceKey: 'codigoNatural',
    order:2,
    label: 'CD Cliente',
  },{
    
    key: 'cediName',
    normaliceKey: 'cediName',
    order: 2,
    label: 'CEDI',
  },{
    
    key: 'storeName',
    normaliceKey: 'storeName',
    order: 3,
    label: 'Tienda',
  },
  // {
    
  //   key: 'vendorCode',
  //   normaliceKey: 'vendorCode',
  //   order: 4,
  //   label: 'Zona',
  // }
  ];
  readonly recognitionId = signal('');
  readonly isLoading = signal(true);
  readonly loadingState = signal<'loading' | 'ready' | 'timeout'>('loading');
  readonly loadingMessage = computed(() => {
    if (this.loadingState() === 'timeout') {
      return 'No hay resultados aún. Puede que el reconocimiento no exista o siga procesándose.';
    }
    return 'Cargando resultados del reconocimiento...';
  });
  readonly zoomLevel = signal(1);
  readonly minZoom = 1;
  readonly maxZoom = 6;
  readonly zoomStep = 0.2;
  readonly images = signal<ImageRecognitionExtend[]>([]);
  readonly selectedImage = signal<ImageRecognitionExtend | null>(null);
  readonly selectedIndex = signal(0);
  readonly recognitionDetails = signal<Record<string | number, RecognitionDetails>>({});
  readonly selectedBoxId = signal<string | number | null>(null);
  readonly hiddenBoxIds = signal<Set<string | number>>(new Set());
  readonly imageSize = signal<ImageSize | null>(null);
  readonly details = computed(() => {
    const image = this.selectedImage();
    if (!image) return null;
    return this.recognitionDetails()[image.image_id] ?? null;
  });

  readonly imageSrc = computed(() => this.selectedImage()?.url ?? '');
  readonly boxes = computed(() => {
    const hidden = this.hiddenBoxIds();
    return this.buildBoxes()
    .filter((box) => box.metadata?.['task_rule_inferences'] === true)
    .filter((box) => !hidden.has(box.id));
  });
  readonly rules = computed(() => this.details()?.rules ?? []);
  private loadingTimeoutId?: number;

  constructor(
    private route: ActivatedRoute,
    private recognition: RecognitionSystemLibraryService,
    private snackBar: MatSnackBar,
    private visualizerService: PageVisualizerService,
    @Inject(DOCUMENT) private document: Document,
  ) {}

  ngOnInit(): void {
    // En modo dialog el ID viene por @Input eventId → manualId$.
    // En modo ruta el ID viene de ActivatedRoute.
    // Nunca mezclamos ambos: si isDialogMode=true ignoramos la ruta.
    const routeId$ = this.isDialogMode
      ? EMPTY
      : this.route.paramMap.pipe(map((params) => params.get('id') ?? ''));

    const recognitionId$ = merge(this.manualId$, routeId$).pipe(
      filter((id) => id.length > 0),
      distinctUntilChanged(),
      tap((id) => this.resetState(id)),
    );

    this.subs.add(
      recognitionId$
        .pipe(switchMap((id) => this.recognition.getRecognitionDetailsLive(id)))
        .subscribe((payload) => this.updateRecognitionDetails(payload))
    );
    this.subs.add(
      recognitionId$
        .pipe(switchMap((id) => this.recognition.getRecognitionImagesWithImagesLive(id)))
        .subscribe((payload) => this.updateImages(payload))
    );

    if (window.matchMedia && window.matchMedia('(max-width: 600px)').matches) {
      this.document.addEventListener('gesturestart', this.gestureHandler, { passive: false });
      this.document.addEventListener('gesturechange', this.gestureHandler, { passive: false });
      this.document.addEventListener('gestureend', this.gestureHandler, { passive: false });
    }
  }
  private readonly gestureHandler = (event: Event) => {
    event.preventDefault();
  };

  ngOnDestroy(): void {
    this.subs.unsubscribe();
    this.document.removeEventListener('gesturestart',  this.gestureHandler);
    this.document.removeEventListener('gesturechange', this.gestureHandler);
    this.document.removeEventListener('gestureend',    this.gestureHandler);
  }

  onBoxClick(box: ImageVisualizerBox): void {
    this.selectedBoxId.set(box.id);
  }

  onImageLoadedFromVisualizer(size: { width: number, height: number }): void {
    // Calcular altura recomendada
    // Ancho del viewport = window.innerWidth
    // Ancho del dialog = 90vw
    // Ancho del canvas = 60% del dialog (aprox, el panel toma 40%)
    const isMobile = window.innerWidth <= 600;
    let canvasWidth = isMobile ? window.innerWidth * 0.9 : window.innerWidth * 0.9 * 0.6;
    
    // Altura proporcional
    const aspect = size.width / size.height;
    let expectedImgHeight = canvasWidth / aspect;
    
    // Sumar headers y controles (~80px)
    let totalHeight = expectedImgHeight + 80;

    // Si es movil, el panel va abajo, asi que sumamos ~300px del panel
    if (isMobile) totalHeight += 300;

    // Respetar limites: minimo 400px, maximo 90vh
    const minH = 400;
    const maxH = window.innerHeight * 0.9;
    
    let finalHeight = Math.max(minH, Math.min(totalHeight, maxH));
    this.recommendedHeight.emit(`${finalHeight}px`);
  }

  onBoxHover(_box: ImageVisualizerBox | null): void {
    // Placeholder for tooltip-driven state or external sync.
  }

  onBoxToggle(payload: { id: string | number; visible: boolean }): void {
    this.hiddenBoxIds.update((current) => {
      const next = new Set(current);
      if (payload.visible) {
        next.delete(payload.id);
      } else {
        next.add(payload.id);
        if (this.selectedBoxId() === payload.id) {
          this.selectedBoxId.set(null);
        }
      }
      return next;
    });
  }

  private resetState(recognitionId: string): void {
    this.recognitionId.set(recognitionId);
    this.images.set([]);
    this.selectedImage.set(null);
    this.recognitionDetails.set({});
    this.selectedBoxId.set(null);
    this.setLoadingState('loading');
    this.startLoadingTimeout();
  }

  private updateImages(images: ImageRecognitionExtend[]): void {
    this.images.set(images);

    if (!images.length) {
      this.selectedImage.set(null);
      this.selectedIndex.set(0);
      this.updateLoadingState();
      return;
    }

    const current = this.selectedImage();
    if (!current) {
      this.selectedImage.set(images[0]);
      this.selectedIndex.set(0);
      this.updateLoadingState();
      return;
    }

    const matchIndex = images.findIndex((img) => img.image_id === current.image_id);
    if (matchIndex === -1) {
      this.selectedImage.set(images[0]);
      this.selectedIndex.set(0);
      this.updateLoadingState();
      return;
    }

    this.selectedImage.set(images[matchIndex]);
    this.selectedIndex.set(matchIndex);
    this.updateLoadingState();
  }

  private updateRecognitionDetails(details: RecognitionDetails[]): void {
    const mapped = details.reduce((result, item) => {
      if (item.image?.image_id !== undefined && item.image?.image_id !== null) {
        result[item.image.image_id] = item;
      }
      return result;
    }, {} as Record<string | number, RecognitionDetails>);

    this.recognitionDetails.set(mapped);
    this.updateLoadingState();
  }

  private updateLoadingState(): void {
    const detail = this.details();
    if (!detail) {
      if (this.loadingState() !== 'timeout') {
        this.setLoadingState('loading');
      }
      return;
    }

    const hasInferences = Array.isArray(detail.inferences) && detail.inferences.length > 0;
    const isFinished = detail.finished_at !== undefined && detail.finished_at !== null;

    if (hasInferences || isFinished) {
      this.setLoadingState('ready');
      return;
    }
    if (this.loadingState() !== 'timeout') {
      this.setLoadingState('loading');
    }
  }

  private startLoadingTimeout(): void {
    if (this.loadingTimeoutId) {
      window.clearTimeout(this.loadingTimeoutId);
    }
    this.loadingTimeoutId = window.setTimeout(() => {
      if (this.loadingState() !== 'ready') {
        this.setLoadingState('timeout');
      }
    }, 45000);
  }

  private setLoadingState(state: 'loading' | 'ready' | 'timeout'): void {
    this.loadingState.set(state);
    this.isLoading.set(state !== 'ready');
    if (state === 'ready' && this.loadingTimeoutId) {
      window.clearTimeout(this.loadingTimeoutId);
      this.loadingTimeoutId = undefined;
    }
  }

  private buildBoxes(): ImageVisualizerBox[] {
    const image = this.selectedImage();
    if (!image) return [];

    const detail = this.recognitionDetails()[image.image_id];
    const inferences = detail?.inferences ?? [];

    return this.visualizerService.mapInferencesToBoxes(inferences);
  }

  selectImageByIndex(index: number): void {
    const list = this.images();
    if (!list.length) return;
    const clamped = Math.max(0, Math.min(index, list.length - 1));
    this.selectedImage.set(list[clamped]);
    this.selectedIndex.set(clamped);
    this.updateLoadingState();
  }

  selectImage(image: ImageRecognitionExtend): void {
    const list = this.images();
    const index = list.findIndex((img) => img.image_id === image.image_id);
    if (index === -1) return;
    this.selectedImage.set(list[index]);
    this.selectedIndex.set(index);
    this.updateLoadingState();
  }

  prevImage(): void {
    this.selectImageByIndex(this.selectedIndex() - 1);
  }

  nextImage(): void {
    this.selectImageByIndex(this.selectedIndex() + 1);
  }

  trackByImage(_index: number, item: ImageRecognitionExtend): number {
    return item.image_id ?? item.id ?? _index;
  }

  zoomIn(): void {
    this.setZoom(this.zoomLevel() + this.zoomStep);
  }

  zoomOut(): void {
    this.setZoom(this.zoomLevel() - this.zoomStep);
  }

  resetZoom(): void {
    this.setZoom(1);
  }

  onZoomChange(value: number): void {
    this.zoomLevel.set(this.normalizeZoom(value));
  }

  private setZoom(value: number): void {
    this.zoomLevel.set(this.normalizeZoom(value));
  }

  private normalizeZoom(value: number): number {
    const clamped = Math.min(Math.max(value, this.minZoom), this.maxZoom);
    return Number(clamped.toFixed(2));
  }

  copyIdRecon(id: string | undefined | null): void {
    if (!id) return;
    navigator.clipboard.writeText(id).then(() => {
      this.snackBar.open('Id copiado con éxito.', 'ok', { duration: 2000 });
    });
  }

  onShared(): void {
    if (!navigator.share) {
      this.copyToClipboard();
      return;
    }
    this.shareUrl();
  }

  private copyToClipboard(): void {
    const url = window.location.href;
    navigator.clipboard.writeText(url).then(
      () => {
        this.snackBar.open('URL copiada al portapapeles.', 'ok', { duration: 2000 });
      },
      () => {
        this.snackBar.open('No se pudo copiar la URL.', 'Cerrar', { duration: 3000 });
      },
    );
  }

  private shareUrl(): void {
    const url = window.location.href;
    const details = this.details();
    navigator.share?.({
      title: '¡Mira mis resultados!',
      text: `Encuentra aquí los resultados de tu reconocimiento: ${details?.recognition_id ?? ''}`,
      url,
    })
      .catch(() => {
        this.snackBar.open('No se pudo compartir la URL.', 'Cerrar', { duration: 3000 });
      });
  }

  calculateDurationInSeconds(startedAt?: Date | string, finishedAt?: Date | string): number {
    return this.visualizerService.calculateDurationInSeconds(startedAt, finishedAt);
  }

  async downloadCsv(): Promise<void> {
    const id = this.recognitionId();
    if (!id) return;
    await this.visualizerService.downloadCsv(id);
  }
}
